import { Types, HydratedDocument, LeanDocument, Branded, MakeOptional, Merge } from "../../types/_utils.types";
import { Resource } from "../stage.dto";
import { TeamRole } from "../application.dto";

export enum MemberRole {
  /** Whom to blame */
  OWNER = "owner",
  /** Makes sure task is done */
  VERIFIER = "verifier",
  /** Does the work */
  ACTOR = "actor",
}

export enum MemberKind {
  /** Other party, mostly the client */
  EXTERNAL = "external",
  /** Inside the team */
  INTERNAL = "internal",
}

export enum MemberTarget {
  ROLE = "role",
  INDIVIDUAL = "individual",
}

export type TaskItemMember = {
  role: MemberRole;
  /** If it's external then task is for the client */
  kind: MemberKind;
  /** does not matter when kind == external */
  target: MemberTarget;
  /** When target == role */
  assigneeRole?: TeamRole;
  /** When target == individual */
  assigneeId?: Types.ObjectId;
};

export enum ActivationCriteriaCategory {
  /** Requires no extra variables */
  PARENT_TASK_ACTIVE = "parent_task_active",
  /** Requires no extra variables */
  PREVIOUS_TASK_COMPLETE = "previous_task_complete",
  /**
   * Requires a task, and it's desired outcome.
   * More dynamic version of "previous_task_complete"
   *
   * @example
   * TASK-2 achieved SUCCESSFUL outcome, activating TASK-3
   */
  OUTCOME_ACHIEVED = "outcome_achieved",
}

export enum CompletionCriteriaCategory {
  /** Requires no extra variable */
  SUB_TASKS_COMPLETED = "all_subtask_complete",
  /**
   * Requires a member role and their action/outcome
   *
   * @example
   * VERIFIER marked PASSED, completing current task
   */
  ACTION_BY_MEMBER = "member_caused_action",
}

export type ActivationCriteria =
  | {
      category: ActivationCriteriaCategory.PARENT_TASK_ACTIVE | ActivationCriteriaCategory.PREVIOUS_TASK_COMPLETE;
    }
  | {
      category: ActivationCriteriaCategory.OUTCOME_ACHIEVED;
      task: Types.ObjectId;
      outcome: Types.ObjectId;
    };

export type CompletionCriteria =
  | {
      category: CompletionCriteriaCategory.SUB_TASKS_COMPLETED;
    }
  | {
      category: CompletionCriteriaCategory.ACTION_BY_MEMBER;
      member: MemberRole;
      outcome: Types.ObjectId;
    };

/** BaseCriteria will always have the `category` field */
export type NestedCriteria<BaseCriteria extends { category: string }> = {
  /**
   * Typical OR would have the value 1
   *
   * AND would have the length of `options`
   */
  minimumRequired: number;
  options: Array<NestedCriteria<BaseCriteria> | BaseCriteria>;
};

export enum TaskStatus {
  PENDING = "pending",
  ACTIVE = "active",
  SKIPPED = "skipped",
  DISQUALIFIED = "disqualified",
  COMPLETE = "complete",
}

export type TaskItemCode = Branded<string, "task_item_code">;

export type TaskItem = {
  /**
   * '-' separate uppercase letters, always starts with group code
   */
  code: TaskItemCode;
  name: string;
  description?: string;
  members: TaskItemMember[];
  group: Types.ObjectId;
  /** Only parent tasks have owner */
  parent: Types.ObjectId | null;
  previous: Types.ObjectId | null;
  /** Just to show in UI whether the task is supposedly completed */
  relatedStage?: Types.ObjectId;
  activationCriteria?: NestedCriteria<ActivationCriteria>;
  completionCriteria?: NestedCriteria<CompletionCriteria>;
  meta: Record<string, unknown>;
};

export type TaskItemDto = LeanDocument<HydratedDocument<TaskItem>>;
export type CreateTaskItemDto = MakeOptional<TaskItemDto, "_id">;

export type TaskGroupCode = Branded<string, "task_group_code">;

export type TaskGroup = {
  name: string;
  /** 3 uppercase letters, unique per group */
  code: TaskGroupCode;
  meta: Record<string, unknown>;
};

export type TaskGroupDto = LeanDocument<HydratedDocument<TaskGroup>>;
export type CreateTaskGroupDto = MakeOptional<TaskGroupDto, "_id">;

export type TaskTemplate = {
  tasks: TaskItemDto[];
  groups: TaskGroupDto[];
  resource: Resource;
  autoStart: boolean;

  organisation: Types.ObjectId;
  createdBy: Types.ObjectId;
};

export type TaskTemplateDoc = HydratedDocument<TaskTemplate>;
export type TaskTemplateDto = LeanDocument<TaskTemplateDoc>;
export type CreateTaskTemplateDto = Merge<
  MakeOptional<TaskTemplateDto, "_id">,
  {
    groups: CreateTaskGroupDto[];
    tasks: CreateTaskItemDto[];
  }
>;
